/**
 * Asset loading with caching. GLBs live in public/models/<stage>/<name>.glb and
 * may be meshopt-compressed (see art/README.md). Each load returns a fresh clone
 * (skinned meshes supported) that shares GPU resources with the cache; shared
 * resources are flagged userData.evoShared so stage disposal won't free them.
 */
import { type AnimationClip, type Material, Mesh, type Object3D, Texture, TextureLoader, SRGBColorSpace } from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';

export interface ModelAsset {
  scene: Object3D;
  animations: AnimationClip[];
}

function markShared(root: Object3D): void {
  root.traverse((o) => {
    if (o instanceof Mesh) {
      o.geometry.userData.evoShared = true;
      const mats = (Array.isArray(o.material) ? o.material : [o.material]) as Material[];
      for (const m of mats) {
        m.userData.evoShared = true;
        for (const v of Object.values(m as any)) {
          if (v instanceof Texture) v.userData.evoShared = true;
        }
      }
    }
  });
}

export class Assets {
  private gltfLoader = new GLTFLoader();
  private textureLoader = new TextureLoader();
  private gltfCache = new Map<string, Promise<GLTF>>();
  private texCache = new Map<string, Promise<Texture>>();

  constructor(private readonly base = import.meta.env.BASE_URL ?? '/') {
    this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
  }

  url(path: string): string {
    if (/^(https?:|data:|blob:)/.test(path)) return path;
    return `${this.base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  private loadGltf(path: string, onProgress?: (f: number) => void): Promise<GLTF> {
    const url = this.url(path);
    let p = this.gltfCache.get(url);
    if (!p) {
      p = new Promise<GLTF>((resolve, reject) => {
        this.gltfLoader.load(
          url,
          (g) => {
            markShared(g.scene);
            resolve(g);
          },
          (e) => {
            if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
          },
          reject,
        );
      });
      this.gltfCache.set(url, p);
      p.catch(() => this.gltfCache.delete(url));
    }
    return p;
  }

  /** Load a GLB and return an independent clone (safe to add to the scene). */
  async model(path: string, onProgress?: (f: number) => void): Promise<ModelAsset> {
    const g = await this.loadGltf(path, onProgress);
    return { scene: skeletonClone(g.scene), animations: g.animations };
  }

  /** Like model() but resolves to undefined instead of throwing (use with a procedural fallback). */
  async tryModel(path: string): Promise<ModelAsset | undefined> {
    try {
      return await this.model(path);
    } catch (err) {
      console.warn(`[assets] missing model ${path}, using fallback`);
      return undefined;
    }
  }

  texture(path: string, srgb = true): Promise<Texture> {
    const url = this.url(path);
    let p = this.texCache.get(url);
    if (!p) {
      p = this.textureLoader.loadAsync(url).then((t) => {
        if (srgb) t.colorSpace = SRGBColorSpace;
        t.userData.evoShared = true;
        return t;
      });
      this.texCache.set(url, p);
    }
    return p;
  }

  /**
   * Free every cached GLB/texture from the GPU and forget them. Called by Game between stages so a
   * long run doesn't keep every stage's models resident (mobile memory). Live clones still work:
   * three re-uploads disposed resources if they're rendered again.
   */
  async purge(): Promise<void> {
    const gltfs = [...this.gltfCache.values()];
    const texes = [...this.texCache.values()];
    this.gltfCache.clear();
    this.texCache.clear();
    for (const p of gltfs) {
      const g = await p.catch(() => undefined);
      g?.scene.traverse((o) => {
        if (o instanceof Mesh) {
          o.geometry.dispose();
          for (const m of (Array.isArray(o.material) ? o.material : [o.material]) as Material[]) {
            for (const v of Object.values(m as any)) if (v instanceof Texture) v.dispose();
            m.dispose();
          }
        }
      });
    }
    for (const p of texes) (await p.catch(() => undefined))?.dispose();
  }

  /** Warm the cache for a list of GLBs (e.g. next stage while a transition plays). */
  async preload(paths: string[]): Promise<void> {
    await Promise.allSettled(paths.map((p) => this.loadGltf(p)));
  }
}
